import { useState } from 'react';

import { LibraryPicker } from '@/components/library-picker';
import { RichTextEditor } from '@/components/rich-text-editor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { MediaRef } from '@/lib/media';

export interface ProjectValues {
  name: string;
  status: string;
  description: string;
  votingStarts: string;
  votingEnds: string;
  heroMedia: MediaRef | null;
  gallery: MediaRef[];
}

const statuses = ['Coming Soon', 'Voting', 'Winner Selected', 'Production', 'Archive'] as const;
const DEFAULT_STATUS = 'Coming Soon';
const STEPS = ['Details', 'Description', 'Media', 'Voting period'] as const;

interface DateErrors {
  votingStarts?: string;
  votingEnds?: string;
}

function todayISO(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function validateDates(starts: string, ends: string): DateErrors {
  const errors: DateErrors = {};
  const today = todayISO();

  if (starts && starts < today) {
    errors.votingStarts = "Voting can't start in the past.";
  }

  if (ends && starts && ends <= starts) {
    errors.votingEnds = 'Voting must end after it starts.';
  } else if (ends && ends < today) {
    errors.votingEnds = "Voting can't end in the past.";
  }

  return errors;
}

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialValues?: Partial<ProjectValues>;
  onSubmit: (values: ProjectValues) => void;
}

export function ProjectDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
}: ProjectDialogProps) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(initialValues?.name ?? '');
  const [status, setStatus] = useState(initialValues?.status ?? DEFAULT_STATUS);
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [votingStarts, setVotingStarts] = useState(initialValues?.votingStarts ?? '');
  const [votingEnds, setVotingEnds] = useState(initialValues?.votingEnds ?? '');
  const [heroMedia, setHeroMedia] = useState<MediaRef | null>(initialValues?.heroMedia ?? null);
  const [gallery, setGallery] = useState<MediaRef[]>(initialValues?.gallery ?? []);
  const [touched, setTouched] = useState<{ votingStarts?: boolean; votingEnds?: boolean }>({});

  const errors = validateDates(votingStarts, votingEnds);
  const startsError = touched.votingStarts ? errors.votingStarts : undefined;
  const endsError = touched.votingEnds ? errors.votingEnds : undefined;

  const isEdit = mode === 'edit';
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isLast) {
              setStep((value) => value + 1);
              return;
            }
            setTouched({ votingStarts: true, votingEnds: true });
            if (errors.votingStarts || errors.votingEnds) return;
            onSubmit({ name, status, description, votingStarts, votingEnds, heroMedia, gallery });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit project' : 'New project'}</DialogTitle>
            <DialogDescription>
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </DialogDescription>
          </DialogHeader>

          {step === 0 && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="project-name">Name</Label>
                <Input
                  id="project-name"
                  name="name"
                  placeholder="e.g. Bloom"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="project-status">Status</Label>
                <Select value={status} onValueChange={setStatus} name="status">
                  <SelectTrigger id="project-status" className="w-full">
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-2">
              <Label>Description</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Short summary of the project"
              />
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Hero image</Label>
                <LibraryPicker value={heroMedia} onChange={setHeroMedia} types={['image']} />
              </div>

              <div className="grid gap-2">
                <Label>Gallery</Label>
                <LibraryPicker multiple values={gallery} onChange={setGallery} types={['image']} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="voting-starts">Voting starts</Label>
                <Input
                  id="voting-starts"
                  name="votingStarts"
                  type="date"
                  value={votingStarts}
                  onChange={(event) => setVotingStarts(event.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, votingStarts: true }))}
                  aria-invalid={Boolean(startsError)}
                  aria-describedby={startsError ? 'voting-starts-error' : undefined}
                />
                <p id="voting-starts-error" className="min-h-4 text-destructive text-xs">
                  {startsError}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="voting-ends">Voting ends</Label>
                <Input
                  id="voting-ends"
                  name="votingEnds"
                  type="date"
                  value={votingEnds}
                  onChange={(event) => setVotingEnds(event.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, votingEnds: true }))}
                  aria-invalid={Boolean(endsError)}
                  aria-describedby={endsError ? 'voting-ends-error' : undefined}
                />
                <p id="voting-ends-error" className="min-h-4 text-destructive text-xs">
                  {endsError}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="justify-between pt-2 sm:justify-between">
            <div>
              {step > 0 && (
                <Button type="button" variant="ghost" onClick={() => setStep((value) => value - 1)}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step === 0 && (
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
              )}
              <Button type="submit">
                {!isLast ? 'Next' : isEdit ? 'Save changes' : 'Create project'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
